import store from '@app/store';
import { ROUTE_PATHS } from '@config/routes';
import {
  applyNotificationNavigation,
  normalizeNotificationPayload,
  resolveNotificationNavigation,
} from '@modules/merchant/notifications/utils/resolveNotificationNavigation';
import resolveMerchantForbiddenRedirectPath from '@modules/merchant/utils/resolveMerchantForbiddenRedirectPath';
import {
  selectActiveBusinessId,
  selectBusinessesHydrated,
  selectMerchantBusinesses,
  switchActiveBusiness,
} from '@redux/merchant/merchantSlice';

let merchantNavigate = null;
let lastForbiddenRedirectAt = 0;
let pendingPushNotification = null;

const FORBIDDEN_REDIRECT_COOLDOWN_MS = 500;

const PUBLIC_PATHS_WITHOUT_REDIRECT = new Set([
  ROUTE_PATHS.LOGIN,
  ROUTE_PATHS.VERIFY_OTP,
  ROUTE_PATHS.FORGOT_PASSWORD,
  ROUTE_PATHS.RESET_PASSWORD,
]);

const PUSH_QUERY_KEYS = [
  'eventType',
  'notificationType',
  'nudgeId',
  'restaurantId',
  'feedId',
  'userId',
  'followerUserId',
  'followerId',
];

export const bindMerchantNavigate = (navigate) => {
  merchantNavigate = navigate;
};

export const unbindMerchantNavigate = () => {
  merchantNavigate = null;
};

export const shouldHandleMerchantForbiddenRedirect = (request) => {
  const path = window.location.pathname;
  if (PUBLIC_PATHS_WITHOUT_REDIRECT.has(path)) return false;
  if (request?.skipForbiddenRedirect) return false;

  const url = String(request?.url || '');
  if (url.includes('/auth/')) return false;

  return true;
};

export const redirectMerchantOnForbidden = () => {
  const now = Date.now();
  if (now - lastForbiddenRedirectAt < FORBIDDEN_REDIRECT_COOLDOWN_MS) return false;

  lastForbiddenRedirectAt = now;

  const state = store.getState();
  const businesses = selectMerchantBusinesses(state);
  const activeBusinessId = selectActiveBusinessId(state);

  // Prefer the first listed business; if that is already active (and caused the
  // 403), fall through to the next entry so recovery actually changes context.
  let nextBusinessId = businesses[0]?.id ?? null;
  if (nextBusinessId && String(nextBusinessId) === String(activeBusinessId ?? '')) {
    nextBusinessId = businesses[1]?.id ?? null;
  }

  if (nextBusinessId) {
    store.dispatch(switchActiveBusiness(nextBusinessId));
  }

  const targetPath = resolveMerchantForbiddenRedirectPath(store.getState());
  const currentPath = window.location.pathname;

  if (currentPath === targetPath) {
    return Boolean(nextBusinessId);
  }

  if (merchantNavigate) {
    merchantNavigate(targetPath, { replace: true });
    return true;
  }

  window.location.replace(targetPath);
  return true;
};

const canApplyPushNavigation = (navigation) => {
  if (!merchantNavigate) return false;
  if (!navigation?.selectBusiness || !navigation.restaurantId) return true;

  const state = store.getState();
  return Boolean(selectBusinessesHydrated(state));
};

/**
 * Opens the in-app destination for a push / deep-link notification payload.
 * Queues until navigate is bound and businesses are hydrated when a business switch is required.
 */
export const openMerchantPushNotification = (rawPayload) => {
  const navigation = resolveNotificationNavigation(rawPayload);
  if (!navigation?.path) return false;

  if (!canApplyPushNavigation(navigation)) {
    pendingPushNotification = rawPayload;
    return true;
  }

  pendingPushNotification = null;

  const state = store.getState();
  return applyNotificationNavigation({
    notification: rawPayload,
    navigate: merchantNavigate,
    dispatch: store.dispatch,
    activeBusinessId: selectActiveBusinessId(state),
    switchBusiness: switchActiveBusiness,
  });
};

export const flushPendingMerchantPushNotification = () => {
  if (!pendingPushNotification) return false;
  return openMerchantPushNotification(pendingPushNotification);
};

/**
 * Reads push deep-link query params from the current URL (and clears them).
 */
export const consumePushNotificationFromUrl = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const hasPushParams = PUSH_QUERY_KEYS.some((key) => params.get(key));
  if (!hasPushParams) return null;

  const payload = normalizeNotificationPayload({
    eventType: params.get('eventType') || params.get('notificationType') || '',
    restaurantId: params.get('restaurantId') || '',
    nudgeId: params.get('nudgeId') || '',
    feedId: params.get('feedId') || '',
    followerUserId: params.get('followerUserId') || '',
    followerId: params.get('followerId') || '',
    userId: params.get('userId') || '',
    notificationData: {
      notificationType: params.get('notificationType') || params.get('eventType') || '',
      restaurantId: params.get('restaurantId') || '',
      nudgeId: params.get('nudgeId') || '',
      feedId: params.get('feedId') || '',
      followerUserId: params.get('followerUserId') || '',
      followerId: params.get('followerId') || '',
      userId: params.get('userId') || '',
    },
  });

  PUSH_QUERY_KEYS.forEach((key) => params.delete(key));
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', nextUrl);

  return payload;
};

export default {
  bindMerchantNavigate,
  unbindMerchantNavigate,
  openMerchantPushNotification,
  flushPendingMerchantPushNotification,
  consumePushNotificationFromUrl,
};
